import {EventEmitter} from "eventemitter3";

import {PlayerController, PlayerControllerEvents} from "./PlayerController.js";

/**
 * A controller for the local, human-controlled player.
 * It uses a strongly-typed EventEmitter to announce when its action state has changed,
 * allowing the networking layer to sync efficiently.
 */
export class LivePlayerController
  extends EventEmitter<PlayerControllerEvents>
  implements PlayerController
{
  public readonly uid: string;
  public recordedActions: boolean[] = [];
  private isTabbing = false;

  constructor(uid: string) {
    super();
    this.uid = uid;
  }

  public getAllActions(): boolean[] {
    return this.recordedActions;
  }

  /**
   * The UI layer (e.g., UserInputCoordinator) calls this method to update the live input state.
   */
  public setTabbing(isTabbing: boolean): void {
    this.isTabbing = isTabbing;
  }

  /**
   * Called by the simulation at each step to get the player's action.
   * This is the source of truth for when the action history changes.
   */
  public getActionForStep(step: number): boolean {
    // Read historical data during rollbacks without emitting events.
    if (step < this.recordedActions.length) {
      return this.recordedActions[step]!;
    }

    // This assertion protects against logic errors in the simulation loop.
    if (step > this.recordedActions.length) {
      throw new Error(
        `[LivePlayerController] A non-sequential step was requested. Expected ${this.recordedActions.length}, got ${step}.`,
      );
    }

    // --- Case 2: The Only Valid "New Action" Path ---
    // At this point, we know `step === this.recordedActions.length`.
    // This is the normal, expected path for generating the next action in the sequence.
    const lastRecordedAction =
      this.recordedActions[this.recordedActions.length - 1] ?? false;
    const currentAction = this.isTabbing;

    this.recordedActions.push(currentAction);

    // Emit the event only if the state has actually flipped.
    if (currentAction !== lastRecordedAction) {
      this.emit("actions-changed");
    }

    return currentAction;
  }

  /**
   * Resets the controller for a new round and emits an event if there were
   * previous actions to clear, signaling the network layer to sync the empty state.
   */
  public reset(): void {
    this.isTabbing = false;
    this.recordedActions = [];
  }
}
